import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft,
  MapPin, 
  Calendar, 
  Users, 
  Clock,
  Building2,
  FileBox,
  Image as ImageIcon,
  CheckCircle2,
  Send,
  HardHat,
  UserCircle,
  UsersRound,
  X,
  Search,
  Star,
  User,
  Lock,
  Shield,
  Camera,
  FileText
} from "lucide-react";
import ProjectProgressFiles from "@/components/ProjectProgressFiles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Roles allowed to view project details with prices
const ALLOWED_ROLES = ["platform_admin", "operations_manager", "team_leader", "worker"];

export default function PublicProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyMessage, setApplyMessage] = useState("");
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  
  // Check if user can see prices (workers/TL/admin only, NOT clients)
  const canSeePrices = user && ALLOWED_ROLES.includes(user.role);
  
  // Check if user is approved (can apply for projects)
  const isApproved = user?.is_approved !== false; // undefined or true = approved
  const canApply = canSeePrices && isApproved;
  
  // Team Leader state
  const [selectedTeamLeader, setSelectedTeamLeader] = useState(null);
  const [leaderCode, setLeaderCode] = useState("");
  const [searchingLeader, setSearchingLeader] = useState(false);
  const [isSelfLeader, setIsSelfLeader] = useState(false);
  
  // Team state
  const [myTeams, setMyTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("");
  
  // Team Members state
  const [workerCode, setWorkerCode] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchingWorker, setSearchingWorker] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/public/projects/${projectId}`);
        setProject(response.data);
        
        // Check if user already applied
        if (user) {
          try {
            const appsRes = await axios.get(`${API}/project-applications`, {
              params: { project_id: projectId }
            });
            const userApp = appsRes.data.find(app => app.user_id === user.user_id);
            if (userApp) setHasApplied(true);
          } catch (e) {
            // No applications endpoint yet or no auth
          }
        }
      } catch (error) {
        console.error("Error fetching project:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId, user]);

  // Fetch team leader profile if current user is team_leader
  useEffect(() => {
    const fetchLeaderProfile = async () => {
      if (applyDialogOpen && user && user.role === "team_leader") {
        try {
          const response = await axios.get(`${API}/team-leaders/me`);
          setSelectedTeamLeader({
            user_id: response.data.user_id,
            name: response.data.name,
            leader_code: response.data.leader_code
          });
          setIsSelfLeader(true);
        } catch (error) {
          console.error("Error fetching leader profile:", error);
        }
      }
    };
    fetchLeaderProfile();
  }, [applyDialogOpen, user]);

  // Fetch my teams when dialog opens
  useEffect(() => {
    const fetchTeams = async () => {
      if (applyDialogOpen && user) {
        try {
          const response = await axios.get(`${API}/teams`, {
            params: { my_teams: true }
          });
          setMyTeams(response.data);
        } catch (error) {
          console.error("Error fetching teams:", error);
        }
      }
    };
    fetchTeams();
  }, [applyDialogOpen, user]);

  // When team is selected, pre-populate members
  useEffect(() => {
    if (selectedTeam) {
      const team = myTeams.find(t => t.team_id === selectedTeam);
      if (team && team.members) {
        setSelectedMembers(team.members.map(m => ({
          user_id: m.user_id,
          name: m.name,
          worker_code: m.worker_code,
          is_available: m.is_available
        })));
      }
    }
  }, [selectedTeam, myTeams]);

  // Search team leader by code
  const handleSearchLeader = async () => {
    if (!leaderCode.trim()) return;
    
    setSearchingLeader(true);
    try {
      const response = await axios.get(`${API}/team-leaders/search`, {
        params: { code: leaderCode.trim().toUpperCase() }
      });
      
      if (response.data.length > 0) {
        const leader = response.data[0];
        setSelectedTeamLeader(leader);
        setLeaderCode("");
        toast.success(`Found: ${leader.name}`);
      } else {
        toast.error("Team Leader not found with this code");
      }
    } catch (error) {
      toast.error("Failed to search Team Leader");
    } finally {
      setSearchingLeader(false);
    }
  };

  const handleSearchWorker = async () => {
    if (!workerCode.trim()) return;
    
    setSearchingWorker(true);
    try {
      const response = await axios.get(`${API}/workers/search`, {
        params: { code: workerCode.trim().toUpperCase() }
      });
      
      if (response.data.length > 0) {
        const worker = response.data[0];
        // Check if already added
        if (!selectedMembers.find(m => m.user_id === worker.user_id)) {
          setSelectedMembers([...selectedMembers, worker]);
          setWorkerCode("");
          toast.success(`${worker.name} added to team`);
        } else {
          toast.info("Worker already in team");
        }
      } else {
        toast.error("Worker not found with this code");
      }
    } catch (error) {
      toast.error("Failed to search worker");
    } finally {
      setSearchingWorker(false);
    }
  };

  const handleRemoveMember = (userId) => {
    setSelectedMembers(selectedMembers.filter(m => m.user_id !== userId));
  };

  const handleClearLeader = () => {
    if (!isSelfLeader) {
      setSelectedTeamLeader(null);
    }
  };

  const handleApply = async () => {
    if (!user) {
      toast.error("Please login to apply for projects");
      navigate("/login");
      return;
    }

    if (!selectedTeamLeader) {
      toast.error("Please select or search for a Team Leader");
      return;
    }

    setApplying(true);
    try {
      await axios.post(`${API}/projects/${projectId}/apply`, {
        project_id: projectId,
        team_leader_id: selectedTeamLeader.user_id,
        team_id: selectedTeam || null,
        team_member_ids: selectedMembers.map(m => m.user_id).filter(Boolean),
        message: applyMessage
      });
      toast.success("Application submitted successfully!");
      setApplyDialogOpen(false);
      setHasApplied(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setApplying(false);
    }
  };

  const formatPrice = (min, max) => {
    // Only show prices to authorized roles
    if (!canSeePrices) {
      return null;
    }
    if (min && max) {
      return `${min.toLocaleString()} - ${max.toLocaleString()} NOK`;
    } else if (min) {
      return `From ${min.toLocaleString()} NOK`;
    } else if (max) {
      return `Up to ${max.toLocaleString()} NOK`;
    }
    return "Price on request";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center">
        <Building2 className="w-16 h-16 text-[#475569] mb-4" />
        <h2 className="text-2xl font-['Unbounded'] text-white mb-4">Project Not Found</h2>
        <Link to="/projects">
          <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
            Browse Projects
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/10">
        <Link to="/" className="flex items-center gap-1">
          <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
            <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
          </div>
          <span className="text-xl font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
        </Link>

        <div className="flex items-center gap-4">
          <Link to="/projects" className="text-[#94A3B8] hover:text-white transition-colors">
            Browse Projects
          </Link>
          {user ? (
            <Link to={
              user.role === "webshop_admin" ? "/admin/webshop" :
              user.role === "rentals_admin" ? "/admin/rentals" :
              user.role === "platform_admin" ? "/admin" :
              user.role === "operations_manager" ? "/admin" :
              user.role === "team_leader" ? "/team-leader" :
              user.role === "worker" ? "/worker" :
              "/client"
            }>
              <Button variant="ghost" className="text-white hover:text-[#23F7DD]">
                Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                Log In
              </Button>
            </Link>
          )}
        </div>
      </nav>

      {/* Back Button */}
      <div className="px-6 md:px-12 py-6">
        <Link to="/projects" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          Back to Projects
        </Link>
      </div>

      {/* Project Content */}
      <div className="px-6 md:px-12 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                    project.project_type === "assembly" 
                      ? "bg-[#23F7DD] text-[#050505]"
                      : project.project_type === "disassembly"
                      ? "bg-[#FF5F1F] text-white"
                      : "bg-purple-500 text-white"
                  }`}>
                    {project.project_type}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                    Open for Applications
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-4">
                  {project.title}
                </h1>
                {project.description && (
                  <p className="text-lg text-[#94A3B8] leading-relaxed">
                    {project.description}
                  </p>
                )}
              </div>

              {/* Details */}
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded']">Project Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <MapPin className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-sm">Location</span>
                      </div>
                      <p className="text-white font-medium">{project.location_address}</p>
                      <p className="text-[#94A3B8]">{project.location_city}</p>
                    </div>

                    <div className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <Calendar className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-sm">Schedule</span>
                      </div>
                      <p className="text-white font-medium">
                        {project.start_date || "Flexible"}
                      </p>
                      {project.end_date && (
                        <p className="text-[#94A3B8]">to {project.end_date}</p>
                      )}
                    </div>

                    <div className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <Users className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-sm">Crew Required</span>
                      </div>
                      <p className="text-white font-medium">{project.required_crew_size} workers</p>
                    </div>

                    <div className="p-4 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                        <Clock className="w-4 h-4 text-[#23F7DD]" />
                        <span className="text-sm">Complexity</span>
                      </div>
                      <p className="text-white font-medium capitalize">{project.complexity}</p>
                      {project.estimated_duration_hours && (
                        <p className="text-[#94A3B8]">~{project.estimated_duration_hours}h</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Price Card - Only for authorized users */}
              {canSeePrices ? (
                <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#4F46E5]/10 border-[#23F7DD]/30">
                  <CardContent className="p-6">
                    <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-2">Project Budget</p>
                    <p className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD] mb-6">
                      {formatPrice(project.budget_min, project.budget_max)}
                    </p>

                    {hasApplied ? (
                      <div className="flex items-center gap-2 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 font-medium">Application Submitted</span>
                      </div>
                    ) : !canApply && user ? (
                      /* User is logged in but not approved yet */
                      <div className="space-y-4">
                        <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                          <div className="flex items-center gap-2 text-yellow-400 mb-2">
                            <Clock className="w-5 h-5" />
                            <span className="font-medium">Approval Pending</span>
                          </div>
                          <p className="text-sm text-[#94A3B8]">
                            Your account is awaiting admin approval. Once approved, you can apply for projects.
                          </p>
                        </div>
                        <Button 
                          disabled 
                          className="w-full bg-[#23F7DD]/50 text-[#050505] font-bold py-6 text-lg cursor-not-allowed"
                        >
                          <HardHat className="w-5 h-5 mr-2" />
                          Waiting for Approval
                        </Button>
                      </div>
                    ) : (
                      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold py-6 text-lg" data-testid="apply-project-btn">
                            <HardHat className="w-5 h-5 mr-2" />
                            Apply for This Project
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="font-['Unbounded'] text-xl">Apply for Project</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-6">
                            <div className="p-4 bg-white/5 rounded-lg">
                              <p className="text-white font-medium">{project.title}</p>
                              <p className="text-sm text-[#94A3B8]">{project.location_city}</p>
                            </div>

                            {!user ? (
                              <div className="text-center py-4">
                                <p className="text-[#94A3B8] mb-4">Please login to apply for projects</p>
                                <Link to="/login">
                                  <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                                    Log In
                                  </Button>
                                </Link>
                              </div>
                            ) : (
                              <>
                                {/* Team Leader Selection - Search by Code */}
                                <div className="space-y-3">
                                  <Label className="text-white flex items-center gap-2">
                                    <UserCircle className="w-4 h-4 text-[#23F7DD]" />
                                    Team Leader <span className="text-red-400">*</span>
                                  </Label>
                                  
                                  {/* If current user is team leader, show them selected */}
                                  {isSelfLeader && selectedTeamLeader && (
                                    <div className="p-3 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <div className="w-10 h-10 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                                            <User className="w-5 h-5 text-[#23F7DD]" />
                                          </div>
                                          <div>
                                            <p className="text-white font-medium">{selectedTeamLeader.name} (You)</p>
                                            <p className="text-xs text-[#23F7DD]">{selectedTeamLeader.leader_code}</p>
                                          </div>
                                        </div>
                                        <CheckCircle2 className="w-5 h-5 text-[#23F7DD]" />
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* If not team leader, show search by code */}
                                  {!isSelfLeader && (
                                    <>
                                      {selectedTeamLeader ? (
                                        <div className="p-3 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                              <div className="w-10 h-10 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                                                <User className="w-5 h-5 text-[#23F7DD]" />
                                              </div>
                                              <div>
                                                <p className="text-white font-medium">{selectedTeamLeader.name}</p>
                                                <p className="text-xs text-[#23F7DD]">{selectedTeamLeader.leader_code}</p>
                                              </div>
                                            </div>
                                            <Button 
                                              variant="ghost" 
                                              size="sm"
                                              onClick={handleClearLeader}
                                              className="text-red-400 hover:text-red-300"
                                            >
                                              <X className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                          <div className="flex gap-2">
                                            <Input
                                              value={leaderCode}
                                              onChange={(e) => setLeaderCode(e.target.value.toUpperCase())}
                                              placeholder="Enter Team Leader code (e.g., TL-001)"
                                              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                                              onKeyDown={(e) => e.key === "Enter" && handleSearchLeader()}
                                            />
                                            <Button 
                                              onClick={handleSearchLeader} 
                                              disabled={searchingLeader}
                                              className="bg-[#23F7DD]/20 text-[#23F7DD] hover:bg-[#23F7DD]/30 border border-[#23F7DD]/30"
                                            >
                                              {searchingLeader ? "..." : <Search className="w-4 h-4" />}
                                            </Button>
                                          </div>
                                          <p className="text-xs text-[#94A3B8]">
                                            Ask your Team Leader for their code (TL-XXX) to add them
                                          </p>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>

                                {/* Team Selection - Only if user is team leader */}
                                {user.role === "team_leader" && myTeams.length > 0 && (
                                  <div className="space-y-2">
                                    <Label className="text-white flex items-center gap-2">
                                      <UsersRound className="w-4 h-4 text-[#23F7DD]" />
                                      Select Your Team (Optional)
                                    </Label>
                                    <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                                      <SelectTrigger className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]">
                                        <SelectValue placeholder="Choose a saved team" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                                        <SelectItem value="none" className="text-[#94A3B8] hover:bg-white/10">
                                          No team (add members manually)
                                        </SelectItem>
                                        {myTeams.map((team) => (
                                          <SelectItem 
                                            key={team.team_id} 
                                            value={team.team_id}
                                            className="text-white hover:bg-white/10"
                                          >
                                            {team.name} ({team.members?.length || 0} members)
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}

                                {/* Add Team Members by Code */}
                                <div className="space-y-2">
                                  <Label className="text-white flex items-center gap-2">
                                    <Search className="w-4 h-4 text-[#23F7DD]" />
                                    Add Team Member by Worker Code
                                  </Label>
                                  <div className="flex gap-2">
                                    <Input
                                      value={workerCode}
                                      onChange={(e) => setWorkerCode(e.target.value.toUpperCase())}
                                      placeholder="e.g., WRK-001"
                                      className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                                      onKeyDown={(e) => e.key === "Enter" && handleSearchWorker()}
                                    />
                                    <Button 
                                      onClick={handleSearchWorker} 
                                      disabled={searchingWorker}
                                      className="bg-[#23F7DD]/20 text-[#23F7DD] hover:bg-[#23F7DD]/30 border border-[#23F7DD]/30"
                                    >
                                      {searchingWorker ? "..." : "Add"}
                                    </Button>
                                  </div>
                                  <p className="text-xs text-[#94A3B8]">Enter the worker&apos;s personal ID code to add them</p>
                                </div>

                                {/* Selected Team Members */}
                                {selectedMembers.length > 0 && (
                                  <div className="space-y-2">
                                    <Label className="text-white">Team Members ({selectedMembers.length})</Label>
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                      {selectedMembers.map((member) => (
                                        <div 
                                          key={member.user_id}
                                          className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                                        >
                                          <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${member.is_available ? 'bg-green-400' : 'bg-red-400'}`} />
                                            <div>
                                              <p className="text-white font-medium">{member.name}</p>
                                              <p className="text-xs text-[#94A3B8]">
                                                {member.worker_code} • {member.is_available ? 'Available' : 'Unavailable'}
                                                {member.average_rating > 0 && (
                                                  <span className="ml-2 inline-flex items-center">
                                                    <Star className="w-3 h-3 text-yellow-400 mr-1" />
                                                    {member.average_rating.toFixed(1)}
                                                  </span>
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                          <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => handleRemoveMember(member.user_id)}
                                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                          >
                                            <X className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Message */}
                                <div className="space-y-2">
                                  <Label className="text-[#94A3B8]">Message (optional)</Label>
                                  <Textarea
                                    value={applyMessage}
                                    onChange={(e) => setApplyMessage(e.target.value)}
                                    placeholder="Introduce yourself, mention your experience..."
                                    rows={3}
                                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                                  />
                                </div>

                                {/* Project Budget Info */}
                                <div className="p-4 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                                  <p className="text-[#94A3B8] text-sm mb-1">Project Budget (Admin Approved)</p>
                                  <p className="text-2xl font-bold text-[#23F7DD]">
                                    kr {(project.budget_display || project.budget_max || project.budget_min || 0).toLocaleString()}
                                  </p>
                                  <p className="text-xs text-[#64748B] mt-1">
                                    This amount will be split among your team members equally.
                                    {selectedMembers.length > 0 && (
                                      <span className="text-[#23F7DD] block mt-1">
                                        ≈ kr {Math.round((project.budget_display || project.budget_max || 0) / (1 + selectedMembers.length)).toLocaleString()} per person ({1 + selectedMembers.length} members)
                                      </span>
                                    )}
                                  </p>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                                  <Button
                                    variant="outline"
                                    onClick={() => setApplyDialogOpen(false)}
                                    className="border-white/20 text-[#94A3B8]"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={handleApply}
                                    disabled={applying || !selectedTeamLeader}
                                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                                  >
                                    {applying ? (
                                      <span className="flex items-center gap-2">
                                        <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                                        Submitting...
                                      </span>
                                    ) : (
                                      <>
                                        <Send className="w-4 h-4 mr-2" />
                                        Accept & Apply
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    <p className="text-xs text-[#94A3B8] text-center mt-4">
                      Your application will be reviewed by our team
                    </p>
                  </CardContent>
                </Card>
              ) : (
                /* Restricted Access Card - for clients and unauthenticated users */
                <Card className="bg-gradient-to-br from-[#4F46E5]/10 to-[#23F7DD]/10 border-[#4F46E5]/30">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Shield className="w-6 h-6 text-[#4F46E5]" />
                      <p className="text-white font-medium">Scaffolders Only</p>
                    </div>
                    <p className="text-[#94A3B8] mb-6">
                      Project budgets and application features are only available to registered scaffolders and team leaders.
                    </p>
                    <div className="space-y-3">
                      {!user ? (
                        <>
                          <Link to="/register?type=worker" className="block">
                            <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold py-4">
                              <HardHat className="w-5 h-5 mr-2" />
                              Register as Scaffolder
                            </Button>
                          </Link>
                          <Link to="/login" className="block">
                            <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 py-4">
                              Already registered? Log In
                            </Button>
                          </Link>
                        </>
                      ) : (
                        <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                          <div className="flex items-center gap-2 text-yellow-400">
                            <Lock className="w-4 h-4" />
                            <span className="text-sm">Client accounts cannot view project pricing or apply for work.</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
        
        {/* Project Progress & Files Section - Only for workers/TL/admin */}
        {canSeePrices && (
          <div className="mt-12">
            <ProjectProgressFiles 
              projectId={projectId} 
              projectTitle={project.title}
              canEdit={user?.role === "team_leader" || user?.role === "worker" || user?.role === "platform_admin" || user?.role === "operations_manager"}
            />
          </div>
        )}
      </div>
    </div>
  );
}
